import type { ReactNode } from 'react';

import { SpinnerGlyph } from 'components/Spinner/SpinnerGlyph';

import { buttonContentRecipe, buttonIconRecipe, buttonSpinnerRecipe } from './Button.recipe';

/**
 * Props are deliberately NOT the discriminated `ContentProps` from `types.ts`. This component only
 * ever receives values already destructured out of a validated union, and a flat object built from
 * variables satisfies no member of a discriminated union — it would not compile. The discrimination
 * belongs on the public prop types; this is the shared implementation behind them.
 */
type Props = {
  iconOnly?: boolean;
  loading?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  children: ReactNode;
};

/**
 * Inner markup shared by `Button` and `ButtonLink`.
 *
 * Extracted so the two components differ ONLY in their outer element. Anything that changes here —
 * the content wrapper carrying the disabled opacity, the icon sizing spans — changes for both, which
 * is what keeps a link styled as a button pixel-identical to the button it imitates.
 */
export const ButtonContent = ({ children, endIcon, iconOnly, loading, startIcon }: Props) => (
  <>
    <span className={buttonContentRecipe({ loading })}>
      {iconOnly ? (
        <span className={buttonIconRecipe()}>{children}</span>
      ) : (
        <>
          {startIcon ? <span className={buttonIconRecipe()}>{startIcon}</span> : null}
          {children}
          {endIcon ? <span className={buttonIconRecipe()}>{endIcon}</span> : null}
        </>
      )}
    </span>
    {/* `aria-hidden` because the busy state is announced by `aria-busy` on the button itself; a
        decorative glyph would otherwise be a second, redundant announcement. The ring is the shared
        `SpinnerGlyph` (Figma's `CircleNotch`), which also carries the rotation. */}
    {loading ? (
      <span aria-hidden className={buttonSpinnerRecipe()}>
        <SpinnerGlyph />
      </span>
    ) : null}
  </>
);
